<template>
  <v-container ref="container" style="max-width: 95%;">
    <h1>Bank Statement Result: {{ uniqueId }}</h1>
    <v-btn
      color="success"
      class="mb-4"
      :href="outputUrl"
      target="_blank"
      :disabled="!isCompleted"
    >
      Export Output
    </v-btn>
    <v-tabs v-model="activeTab" background-color="primary" dark>
      <v-tab>Statement Info</v-tab>
      <v-tab>Transactions</v-tab>
      <v-tab>Supplier List</v-tab>
      <v-tab>Client List</v-tab>
    </v-tabs>
    <v-tabs-items v-model="activeTab">
      <v-tab-item>
        <v-card class="mt-4" outlined>
          <v-card-title>
            Statement Information
            <v-btn icon @click="openEditDialog">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-list>
              <v-list-item v-for="(value, key) in tableData.statement_info" :key="key">
                <v-list-item-content>
                  <v-list-item-title>
                    <strong>{{ formatKey(key) }}:</strong> {{ formatValue(value) }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
            <v-alert v-if="!tableData.statement_info || Object.keys(tableData.statement_info).length === 0" type="info" outlined>
              No statement information available.
            </v-alert>
          </v-card-text>
        </v-card>
      </v-tab-item>
      <v-tab-item>
        <TransactionsTable
          :taskId="taskId"
          :table-data="tableData.account_transaction"
          :fetch-result="fetchResult"
        />
      </v-tab-item>
      <v-tab-item>
        <ListTable :list-data="supplierList" title="Suppliers" />
      </v-tab-item>
      <v-tab-item>
        <ListTable :list-data="clientList" title="Clients" />
      </v-tab-item>
    </v-tabs-items>

    <!-- Edit Dialog -->
    <v-dialog v-model="editDialog" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="headline">Edit Statement Information</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="editForm" v-model="valid" lazy-validation>
            <v-text-field
              v-model="editedInfo.bank_name"
              label="Bank Name"
              :rules="[v => !!v || 'Bank Name is required']"
              required
            ></v-text-field>
            <v-text-field
              v-model="editedInfo.statement_date"
              label="Statement Date"
              type="date"
              :rules="[v => !!v || 'Statement Date is required']"
              required
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="closeEditDialog">Cancel</v-btn>
          <v-btn color="blue darken-1" text @click="updateValue" :disabled="!valid">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import TransactionsTable from './TransactionsTable.vue'
import ListTable from './ListTable.vue'

export default {
  components: { TransactionsTable, ListTable },
  data() {
    return {
      taskId: this.$route.params.id,
      uniqueId: this.$route.params.uniqueId,
      activeTab: 0,
      loading: false,
      outputUrl: "",
      isCompleted: false,
      tableData: { statement_info: {}, account_transaction: [], supplier_list: {}, client_list: {} },
      supplierList: {},
      clientList: {},
      editDialog: false,
      valid: true,
      editedInfo: {
        bank_name: '',
        statement_date: '',
        period: ''
      }
    }
  },
  computed: {
    tableHeight() {
      const offset = 240;
      return `calc(100vh - ${offset}px)`;
    }
  },
  methods: {
    initializeListItem(listData) {
      let returnData = {}
      for (const listKey in listData) {
        let listItem = listData[listKey]
        let transactionList = []
        for (const account of listItem) {
          for (const transaction of account.transactions) {
            transactionList.push({
              accountNumber: account.account_number,
              date: transaction.date,
              type: transaction.type,
              desc: transaction.desc,
              deposit: transaction.deposit,
              withdrawal: transaction.withdrawal,
              currency: account.currency
            })
          }
        }
        returnData[listKey] = transactionList
      }
      return returnData
    },
    formatKey(key) {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());
    },
    formatValue(value) {
      if (value === null || value === undefined) return 'N/A';
      if (typeof value === 'object') return JSON.stringify(value);
      return value.toString();
    },
    async fetchResult() {
      this.loading = true
      try {
        const response = await this.$http.get(`/api/tasks/${this.taskId}/result`)
        this.tableData = response.data
        this.clientList = this.initializeListItem(this.tableData.client_list)
        this.supplierList = this.initializeListItem(this.tableData.supplier_list)
        this.isCompleted = true
      } catch (error) {
        console.error('Error fetching task result:', error)
        alert('Failed to load task result')
      }
      this.loading = false
    },
    openEditDialog() {
      this.editedInfo = {
        bank_name: this.tableData.statement_info.bank_name || '',
        statement_date: this.tableData.statement_info.statement_date || '',
        period: this.tableData.statement_info.period || ''
      }
      this.editDialog = true
    },
    closeEditDialog() {
      this.editDialog = false
      this.$refs.editForm.resetValidation()
    },
    async updateValue() {
      if (this.$refs.editForm.validate()) {
        try {
          for (let infoKey in this.editedInfo) {
            const updatedField = JSON.stringify(['bank_statement', 0, 'statement_info', infoKey])
            await this.$http.post(`/api/tasks/${this.taskId}/update_value`, {
              field: updatedField,
              value: this.editedInfo[infoKey]
            })
          }
          this.editDialog = false
          await this.fetchResult() // Refresh the table data
          alert('Statement information updated successfully')
        } catch (error) {
          console.error('Error updating statement info:', error)
          alert('Failed to update statement information')
        }
      }
    },
    updateContainerHeight() {
      this.containerHeight = this.$refs.container ? this.$refs.container.clientHeight : 0
    },
  },
  mounted() {
    this.fetchResult()
    this.outputUrl = `${this.$http.defaults.baseURL}/api/tasks/${this.$route.params.id}/export`
    window.addEventListener('resize', this.updateContainerHeight)
    this.updateContainerHeight()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateContainerHeight)
  }
}
</script>
